import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, Typography, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Paper, Switch, FormControlLabel, Stack, Snackbar
} from '@mui/material';
import { Lan, Search, SettingsEthernet } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

const wsBaseUrl = 'wss://api1.masternode.wobot.ai';

const defaultAdvanced = {
  targetCIDRs: '',
  port: '',
  timeoutMs: '',
  workers: ''
};

function parseCIDRs(str) {
  return str
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

const CIDRScanPanel = ({ apiKey, clientId, clientName, onError }) => {
  const [mode, setMode] = useState('auto');
  const [advanced, setAdvanced] = useState(defaultAdvanced);
  const [scanning, setScanning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [foundIPs, setFoundIPs] = useState([]); // Streaming IPs
  const [progressData, setProgressData] = useState(null); // Progress info
  const [scanCompletionData, setScanCompletionData] = useState(null); // Summary
  const pendingScanRef = useRef(null);
  const websocketRef = useRef(null);
  const timeoutRef = useRef(null);

  const cleanupConnection = () => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setConnecting(false);
  };

  useEffect(() => () => cleanupConnection(), []);

  const fetchWebSocketTicket = async () => {
    try {
      const response = await fetch(`${wsBaseUrl.replace('wss://', 'https://')}/api/auth/ws-ticket`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to obtain WebSocket ticket: ${response.status} ${response.statusText}`);
      const data = await response.json();
      if (!data.ticket) throw new Error('No ticket received from server');
      return data.ticket;
    } catch (err) {
      setError(`Failed to obtain WebSocket authentication ticket: ${err.message}`);
      if (onError) onError(`WebSocket ticket fetch failed: ${err.message}`);
      throw err;
    }
  };

  const triggerScan = async () => {
    if (!apiKey || !clientId) {
      setError('API key or client ID not specified');
      return;
    }
    cleanupConnection();
    setScanning(true);
    setConnecting(true);
    setError('');
    setScanResults(null);
    setProgress('');
    setFoundIPs([]);
    setProgressData(null);
    setScanCompletionData(null);
    const correlationID = uuidv4();
    pendingScanRef.current = correlationID;
    try {
      const ticket = await fetchWebSocketTicket();
      const wsUrl = `${wsBaseUrl}/ws/admin?ticket=${ticket}`;
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;
      ws.onopen = () => {
        setConnecting(false);
        let payload = {
          targetClientID: clientId,
          correlationID
        };
        if (mode === 'advanced') {
          const cidrs = parseCIDRs(advanced.targetCIDRs);
          if (cidrs.length > 0) payload.targetCIDRs = cidrs;
          if (advanced.port) payload.port = parseInt(advanced.port, 10);
          if (advanced.timeoutMs) payload.timeoutMs = parseInt(advanced.timeoutMs, 10);
          if (advanced.workers) payload.workers = parseInt(advanced.workers, 10);
        }
        ws.send(JSON.stringify({
          action: 'admin_trigger_cidr_scan',
          payload
        }));
        timeoutRef.current = setTimeout(() => {
          if (pendingScanRef.current === correlationID) {
            setError('CIDR scan timed out. Client may be offline or unresponsive.');
            setScanning(false);
            pendingScanRef.current = null;
            cleanupConnection();
          }
        }, 60000); // 60s timeout
      };
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (!message.type || !message.payload) return;
          if (message.payload.correlationID !== correlationID) return;
          // Reset inactivity timeout on any message
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            if (pendingScanRef.current === correlationID) {
              setError('CIDR scan timed out. Client may be offline or unresponsive.');
              setScanning(false);
              pendingScanRef.current = null;
              cleanupConnection();
            }
          }, 60000);

          switch (message.type) {
            case 'cidr_ip_delivery':
              setFoundIPs(prev => [...prev, message.payload.ipAddress + (message.payload.port ? (':' + message.payload.port) : '')]);
              break;
            case 'cidr_scan_progress_update':
              setProgressData(message.payload);
              setProgress(`Scanning ${message.payload.currentCIDR} (${message.payload.cidrsProcessed}/${message.payload.totalCIDRsToScan}), IPs scanned: ${message.payload.ipsScannedInCIDR}, found: ${message.payload.ipsFoundInCIDR}`);
              break;
            case 'cidr_scan_complete':
              setScanCompletionData(message.payload);
              setScanning(false);
              pendingScanRef.current = null;
              cleanupConnection();
              break;
            case 'cidr_scan_error':
              setError(`Error: ${message.payload.error}`);
              setScanning(false);
              pendingScanRef.current = null;
              cleanupConnection();
              break;
            default:
              break;
          }
        } catch (err) {
          // Ignore parse errors
        }
      };
      ws.onclose = (event) => {
        setConnecting(false);
        if (pendingScanRef.current === correlationID && scanning) {
          setError('WebSocket connection closed unexpectedly during scan');
          setScanning(false);
          pendingScanRef.current = null;
        }
        websocketRef.current = null;
      };
      ws.onerror = () => {
        setConnecting(false);
        setScanning(false);
        setError('WebSocket connection error');
        if (pendingScanRef.current === correlationID) {
          pendingScanRef.current = null;
        }
        cleanupConnection();
      };
    } catch (err) {
      setConnecting(false);
      setScanning(false);
      pendingScanRef.current = null;
    }
  };

  const handleAdvancedChange = (e) => {
    setAdvanced({ ...advanced, [e.target.name]: e.target.value });
  };

  const connectionStatus = () => {
    if (connecting) {
      return <Alert severity="info" sx={{ mt: 1 }}>Connecting to server...</Alert>;
    }
    return null;
  };

  if (!clientId) {
    return <Alert severity="info">Select a client to run a CIDR scan</Alert>;
  }

  return (
    <Box sx={{ background: 'rgba(255,255,255,0.02)', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Lan color="primary" sx={{ mr: 1 }} />
        <Typography variant="h7">Network Scan For IP Camera and NVR</Typography>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={mode === 'advanced'} onChange={e => setMode(e.target.checked ? 'advanced' : 'auto')} />}
          label={mode === 'advanced' ? 'Advanced' : 'Auto'}
        />
        <Button
          variant="contained"
          startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <Search />}
          onClick={triggerScan}
          disabled={scanning || connecting}
        >
          {scanning ? 'Scanning...' : (mode === 'auto' ? 'Auto Scan' : 'Advanced Scan')}
        </Button>
      </Stack>
      {mode === 'advanced' && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Target CIDRs (comma, space, or newline separated)"
              name="targetCIDRs"
              value={advanced.targetCIDRs}
              onChange={handleAdvancedChange}
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. 172.16.0.0/22, 192.168.0.0/32"
              disabled={scanning}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Port (optional)"
                name="port"
                value={advanced.port}
                onChange={handleAdvancedChange}
                type="number"
                inputProps={{ min: 1, max: 65535 }}
                disabled={scanning}
              />
              <TextField
                label="Timeout (ms, optional)"
                name="timeoutMs"
                value={advanced.timeoutMs}
                onChange={handleAdvancedChange}
                type="number"
                inputProps={{ min: 10, max: 60000 }}
                disabled={scanning}
              />
              <TextField
                label="Workers (optional)"
                name="workers"
                value={advanced.workers}
                onChange={handleAdvancedChange}
                type="number"
                inputProps={{ min: 1, max: 1000 }}
                disabled={scanning}
              />
            </Stack>
          </Stack>
        </Paper>
      )}
      {scanning && progress && (
        <Alert severity="info" sx={{ mb: 2 }}>{progress}</Alert>
      )}
      {connectionStatus()}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      {(scanCompletionData || foundIPs.length > 0) && (
        <Box sx={{ mt: 2 }}>
          {scanCompletionData && (
            <Alert severity={scanCompletionData.error ? 'error' : 'success'} sx={{ mb: 2 }}>
              {scanCompletionData.error ? `Scan failed: ${scanCompletionData.error}` : `Scan completed for client ${clientName || clientId}`}
            </Alert>
          )}
          {scanCompletionData && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Scanned CIDRs</Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {(scanCompletionData.scannedCIDRs || []).map((cidr, idx) => (
                    <li key={idx}><Typography variant="body2">{cidr}</Typography></li>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Total IPs Attempted</Typography>
                <Typography variant="body2">{scanCompletionData.totalIPsAttempted}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Total Found</Typography>
                <Typography variant="body2">{scanCompletionData.totalOpenFound}</Typography>
              </Box>
            </Stack>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(foundIPs.join('\n'));
                  setCopySnackbar(true);
                }
              }}
              disabled={foundIPs.length === 0}
              sx={{ ml: 'auto' }}
            >
              Copy All
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 300, border: '1px solid #eee', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme => theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[200] }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Camera IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {foundIPs.length > 0 ? (
                  foundIPs.map((ip, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{ip}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">-</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Snackbar
            open={copySnackbar}
            autoHideDuration={2000}
            onClose={() => setCopySnackbar(false)}
            message="Copied!"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CIDRScanPanel;
